
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { HelmetProvider } from "react-helmet-async";
import LoadingBoundary from "@/components/LoadingBoundary";
import { useEffect } from "react";
import { initGA, trackPageView } from "@/lib/analytics";
import { captureLandingAttribution } from "@/lib/attribution";
import { initPostHog, capturePageView } from "@/lib/posthog";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { WelcomeDiscountRibbon } from "@/components/WelcomeDiscountRibbon";
import { SeasonalSaleRibbon } from "@/components/SeasonalSaleRibbon";
import { RecentSalesPopup } from "@/components/RecentSalesPopup";
import { ConsentBanner } from "@/components/ConsentBanner";
import Index from "./pages/Index";
import Dashboard from "./pages/Dashboard";
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import CheckEmail from "./pages/CheckEmail";
import AccountSettings from "./pages/AccountSettings";
import MysteryCreation from "./pages/MysteryCreation";
import MysteryChatPage from "./pages/MysteryChat";
import MysteryPurchase from "./pages/MysteryPurchase";
import MysteryView from "./pages/MysteryView";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Support from "./pages/Support";
import About from "./pages/About";
import CustomMurderMysteryParty from "./pages/CustomMurderMysteryParty";
import OfficeMurderMysteryParty from "./pages/OfficeMurderMysteryParty";
import ColdCaseFiles from "./pages/ColdCaseFiles";
import ColdCaseDelivery from "./pages/ColdCaseDelivery";
import NotFound from "./pages/NotFound";
import AuthCallback from "./pages/AuthCallback";
import CharacterAccess from "./pages/CharacterAccess";
import HostAccess from "./pages/HostAccess";
import BlogIndex from "./pages/BlogIndex";
import BlogPost from "./pages/BlogPost";
import PaymentSuccess from "./pages/PaymentSuccess";
import AdaptationSuccess from "./pages/AdaptationSuccess"; // ADR-0036 Phase B, staging only
import Feedback from "./pages/Feedback";
import GuestFeedback from "./pages/GuestFeedback";
import BillingHistory from "./pages/BillingHistory";
import AdminDashboard from "./pages/AdminDashboard";
import DarkHomePreview from "./pages/DarkHomePreview";
import FontPreview from "./pages/FontPreview";
import HomeParallaxPreview from "./pages/HomeParallaxPreview";
import EmailPreview from "./pages/EmailPreview";
import MysteryRoomHero from "./components/MysteryRoomHero";
import EvidenceCardPreview from "./pages/EvidenceCardPreview";
import GuestDropoutPreview from "./pages/GuestDropoutPreview"; // ADR-0036/0082 mock preview, not the real feature
import EvidenceCardPrint from "./pages/EvidenceCardPrint";
import React from "react";

const queryClient = new QueryClient();

// Protected route wrapper component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  
  return (
    <LoadingBoundary loading={loading}>
      {isAuthenticated ? (
        <>{children}</>
      ) : (
        <Navigate to="/sign-in" replace />
      )}
    </LoadingBoundary>
  );
};

// Track route changes
const RouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    initGA();
    initPostHog();
    captureLandingAttribution();
  }, []);

  useEffect(() => {
    const path = location.pathname + location.search;
    trackPageView(path);
    capturePageView(path);
  }, [location.pathname, location.search]);

  return null;
};

// Main App component with simplified structure
const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <RouteTracker />
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <AppRoutes />
          </TooltipProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </HelmetProvider>
);

// Recent-sales popup is route-gated to high-intent pages only (ADR-0071) —
// not shown on chat/dashboard/account pages where it'd just be noise.
const RECENT_SALES_POPUP_ROUTES = [/^\/$/, /^\/mystery\/purchase\//];

const AppRoutes = () => {
  const { loading, isAuthenticated } = useAuth();
  const location = useLocation();
  const showRecentSalesPopup = RECENT_SALES_POPUP_ROUTES.some((pattern) =>
    pattern.test(location.pathname)
  );

  return (
    <LoadingBoundary loading={loading}>
      <SeasonalSaleRibbon />
      <WelcomeDiscountRibbon />
      <RecentSalesPopup enabled={showRecentSalesPopup} />
      <ConsentBanner />
      {isAuthenticated && <MobileBottomNav />}
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/sign-up" element={<SignUp />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/check-email" element={<CheckEmail />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        {/* Was a fake mock-content demo page (placeholder authors, stock photos),
            never real product content. Redirect instead of 404 for any existing
            bookmarks/indexed links; kept in the GH Pages static-fallback list
            below (scripts/generate-sitemap.mjs) so direct hits don't 404 before
            the SPA router can take over and redirect client-side. */}
        <Route path="/showcase" element={<Navigate to="/custom-murder-mystery-party" replace />} />
        <Route path="/contact" element={<Navigate to="/support" replace />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/custom-murder-mystery-party" element={<CustomMurderMysteryParty />} />
        <Route path="/:lang/custom-murder-mystery-party" element={<CustomMurderMysteryParty />} />
        <Route path="/office-murder-mystery-party" element={<OfficeMurderMysteryParty />} />
        <Route path="/:lang/office-murder-mystery-party" element={<OfficeMurderMysteryParty />} />
        {/* Cold Case Files — solo product (ADR-0029): own landing + guest token delivery */}
        <Route path="/cold-case-files" element={<ColdCaseFiles />} />
        <Route path="/cold-case/:token" element={<ColdCaseDelivery />} />
        <Route path="/support" element={<Support />} />
        <Route path="/dark-preview" element={<DarkHomePreview />} />
        <Route path="/font-preview" element={<FontPreview />} />
        <Route path="/home-parallax" element={<HomeParallaxPreview />} />
        <Route path="/email-preview" element={<EmailPreview />} />
        <Route path="/evidence-card-preview" element={<EvidenceCardPreview />} />
        <Route path="/guest-dropout-preview" element={<GuestDropoutPreview />} />
        <Route path="/evidence-card-print" element={<EvidenceCardPrint />} />
        <Route path="/mystery-hero" element={<div className="bg-[#1a0a1a]"><MysteryRoomHero /><div className="px-6 py-32 text-center"><p className="text-white/20 text-sm">Rest of homepage content would go below</p></div></div>} />
        
        {/* Character access route (public) */}
        <Route path="/character/:token" element={<CharacterAccess />} />
        {/* Short alias for shareable copy-links (ADR-0030); same page, shorter URL */}
        <Route path="/c/:token" element={<CharacterAccess />} />
        {/* Host access route (public) */}
        <Route path="/host/:token" element={<HostAccess />} />
        {/* Feedback route (public, accessed from follow-up emails) */}
        <Route path="/feedback/:conversationId" element={<Feedback />} />

        {/* Guest feedback route (public, accessed from guest follow-up emails) */}
        <Route path="/guest-feedback/:token" element={<GuestFeedback />} />

        {/* Payment success and cancel routes */}
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/adaptation-success" element={<AdaptationSuccess />} />
        <Route path="/payment-canceled" element={<Navigate to="/" replace />} />
        
        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <AccountSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute>
              <BillingHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mystery/create"
          element={
            <ProtectedRoute>
              <MysteryCreation />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/mystery/edit/:id" 
          element={
            <ProtectedRoute>
              <MysteryChatPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/mystery/chat/:id" 
          element={
            <ProtectedRoute>
              <MysteryChatPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/mystery/purchase/:id" 
          element={<MysteryPurchase />} 
        />
        <Route path="/mystery/:id" element={<MysteryView />} />
        <Route path="/:lang/blog" element={<BlogIndex />} />
        <Route path="/:lang/blog/:slug" element={<BlogPost />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/about" element={<About />} />
        <Route path="/:lang/about" element={<About />} />
        
        <Route path="*" element={<NotFound />} />
      </Routes>
    </LoadingBoundary>
  );
};

export default App;
